/* =============================================================================
   ics-messager - the product's message, consent, prompt, toast and modal-dialog
   library. One module, loaded by every page shell inside
   dist-v2/ics-dialogs.min.js (index.dialogs.js, which carries the window
   component in the same bundle so both share ONE Window class, stack and
   z-counter).

   PUBLIC SURFACE - the eleven bare globals installed by `messagerBridge` at the
   bottom of this file:
     IcsMsg(key[, tokens][, fn | opt])          THE DICTIONARY CALL - say a
                                                message by key; the dictionary
                                                owns the wording, the severity
                                                and the appearance. Prefer it to
                                                the ten below in new code.
     IcsAlert(msg[, fnOk])                      one-button message box
     IcsMsgBox(title, msg[, fnOk])              the same box with your own title
     IcsToast(type, msg[, opt])                 passive card, no mask
     IcsToastOk / IcsToastErr / IcsToastWarn / IcsToastInfo (msg[, opt])
     IcsConfirm(msg, fnYes[, {title, yesText, noText, danger, onNo}])
     IcsPrompt(msg, default, fnOk[, {title, okText, cancelText, placeholder,
                                     maxlength, onCancel}])
     IcsModalDialog(url, arg, features, fnDone) a page framed in a modal window

   A STATIC API: nothing binds to an element, nothing is authored in markup,
   there is no Registry entry. Boxes are composed on the own ics-window: they
   share its z-counter (a box always renders above an open modal window and
   below the dropdown band), its mask, drag and centering. Every call builds
   FRESH elements and destroys them on close, so no state survives a box.

   THE RULES CALLERS MUST KNOW
   - Nothing blocks. Anything that must wait for the click - navigation,
     reload, window.close, submit - belongs in the callback.
   - A blank or whitespace-only message opens NO box, but the callback still
     runs: the callback carries the flow the click would have released. A blank
     toast is the deliberate opposite - it shows a readable fallback sentence.
   - Messages are ESCAPED by default; opt in to markup with `html: true`.
   - Escape in the confirm family PRESSES No: fn(false) / onNo runs. Dismissing
     a plain message box never invokes the callback.
   - The box closes BEFORE the callback runs, so a callback may open its own
     window or progress bar without a mask lingering underneath.
   - Buttons: the affirmative first (left), the dismissal second (right); the
     affirmative is focused on open, so Enter answers it.
   - Geometry: message box 380px, confirm and prompt 400px (BOX_WIDTH),
     content-high, centered, draggable, not resizable, no corner close chip -
     the footer buttons and Escape are the only ways out.
   - A 2-argument message renders no icon and no reserved gutter for one.
   - `.messager-body` is a PUBLIC class on the content element: page-level
     overrides target it, so it must stay.
   - Toasts take no key bindings at all: Escape must never close one.

   `messager` (the alert/confirm/show statics) is the internal composition core
   the bridge globals are built on. It is published as `$.messager` only when
   that name is free; an existing implementation is never clobbered.
   ============================================================================= */
import { Window } from './ics-window.js';
import { WindowStackService } from '../services/windowstack.js';
import { MessageCatalogService } from '../i18n/l10n/localisation-map.js';
import productMessages from '../i18n/messages.en.js';

/* THE PRODUCT MESSAGE DICTIONARY is registered HERE, once, from the one bundle
   every shell loads. The catalog store is realm-shared, so registering it in
   this bundle makes it resolvable from every other bundle's `t()` too - while
   the generated payload stays out of their bytes. */
MessageCatalogService.extendIfAbsent('en', productMessages);

const boxes = [];                        // open alert/confirm stack; last = topmost

/** true while an alert/confirm/prompt box is open - the shell's Escape guard asks
    this so the central KeyService lets the box's own handler answer the key
    (Esc ≡ No / dismiss) instead of closing it as a plain window. Pure (no DOM read). */
export function hasOpenBox() { return boxes.length > 0; }

const ESC_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ESC_MAP[c]);
}

/* =============================================================================
   THE TOAST - the product's passive notice
   =============================================================================
   Card = .ics-toast plus the severity class .ics-toast--success|error|warning|
   info (the .ics-messager--toast / .ics-messager--<type> names ride along for
   test selectors): the severity glyph from the own ics-icons set on a badge -
   success = ok-check, error = back-undo, warning = notify, info = info -
   a bold title line, the message, and a × that appears on hover. The card is
   painted from the design tokens by css/components/ics-messager.css; the severity
   class is the sheet's only cue, so never hardcode a colour on the card.

   Host = #ics-toast-host, ONE fixed flex column at the viewport's bottom-right
   (z = --ics-z-toast): the newest card lands nearest the corner and older ones
   move up; at most TOAST_MAX_VISIBLE cards stand at once, the oldest closing to
   make room. Timing: error 5200 ms, everything else 3400 ms, 0 = never; hover
   pauses the timer and leaving restarts it in FULL. Clicking a card runs its
   onClick (when given) and dismisses it. Every toast is recorded in
   `toastHistory`. A toast has no mask and takes no key bindings.
   ============================================================================= */
export const TOAST_TITLE = { success: 'Success', error: 'Error', warning: 'Warning', info: 'Info' };
/** the severity glyph, by ics-icons name - the card AND the recall list use it */
export const TOAST_ICON = { success: 'ok-check', error: 'back-undo', warning: 'notify', info: 'info' };
export const TOAST_MAX_VISIBLE = 6;
const TOAST_ANIM_MS = 200;
const liveToasts = [];                   // open cards, oldest first
let toastHost = null;

/** toast option merge - pure: type ∈ TOAST_TITLE (else info), title = the severity
    default when blank, timeout = the severity default when not a number */
export function normToast(options) {
  const o = Object.assign({ type: 'info', title: undefined, msg: '', timeout: undefined, html: undefined, onClick: null }, options || {});
  if (!TOAST_TITLE[o.type]) o.type = 'info';
  if (o.title === undefined || o.title === null || String(o.title).trim() === '') o.title = TOAST_TITLE[o.type];
  else o.title = String(o.title);
  if (typeof o.timeout !== 'number' || isNaN(o.timeout)) o.timeout = o.type === 'error' ? 5200 : 3400;
  return o;
}

function getToastHost() {
  if (toastHost && toastHost.parentNode) return toastHost;
  toastHost = document.getElementById('ics-toast-host');
  if (!toastHost) {
    toastHost = document.createElement('div');
    toastHost.id = 'ics-toast-host';
    toastHost.className = 'ics-toast-host';
    toastHost.setAttribute('aria-live', 'polite');
    document.body.appendChild(toastHost);
  }
  return toastHost;
}

/* ---- the recall list: the last TOAST_HISTORY_MAX toasts, per user, persistent ------
   Storage = localStorage under 'ics.toast.history:<user>' - per browser, persistent,
   the same list in every tab - where <user> is the global `IcsUserKey` (the
   session user id, assigned by the page shell), else html[data-ics-user], else
   'anon'. Resolved at every read/write, so the order
   of script loading does not matter. No storage (privacy mode / quota) = a
   per-page memory list, never an error. Newest first. Subscribers + a document
   'ics:toast' CustomEvent {kind:'push'|'clear', entry} let a shell repaint live. */
export const TOAST_HISTORY_MAX = 10;
const HISTORY_KEY_PREFIX = 'ics.toast.history:';
const historySubs = new Set();
let memHistory = [];
/* set once a write has actually FAILED (quota, privacy mode, a locked profile). A store can be
   readable and unwritable at the same time, and then the stale stored list would out-rank the
   fresher memory copy on every read - so once writing is known to fail, memory IS the list. */
let historyMemOnly = false;

function historyScope() {
  const k = globalThis.IcsUserKey;
  if (k !== undefined && k !== null && String(k).trim() !== '') return String(k).trim();
  try {
    const a = document.documentElement.getAttribute('data-ics-user');
    if (a) return a;
  } catch (e) { /* no document */ }
  return 'anon';
}
function historyStorage() {
  try { return globalThis.localStorage || null; } catch (e) { return null; }
}
function readHistory() {
  const st = historyStorage();
  if (!st || historyMemOnly) return memHistory;
  try {
    const raw = st.getItem(HISTORY_KEY_PREFIX + historyScope());
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch (e) { return memHistory; }
}
function writeHistory(arr) {
  memHistory = arr;
  const st = historyStorage();
  if (!st) return;
  try {
    st.setItem(HISTORY_KEY_PREFIX + historyScope(), JSON.stringify(arr));
    historyMemOnly = false;
  } catch (e) {
    historyMemOnly = true;          // readable but unwritable: memory is the truth from here on
  }
}
function notifyHistory(kind, entry) {
  historySubs.forEach((fn) => { try { fn(kind, entry); } catch (e) { console.error('[ics] toastHistory subscriber failed', e); } });
  try { document.dispatchEvent(new CustomEvent('ics:toast', { detail: { kind, entry } })); } catch (e) { /* no DOM events here */ }
}
export const toastHistory = {
  MAX: TOAST_HISTORY_MAX,
  /** per-type presentation the recall panel reuses: {title, icon (ics-icons name)} */
  types: { success: { title: TOAST_TITLE.success, icon: TOAST_ICON.success }, error: { title: TOAST_TITLE.error, icon: TOAST_ICON.error }, warning: { title: TOAST_TITLE.warning, icon: TOAST_ICON.warning }, info: { title: TOAST_TITLE.info, icon: TOAST_ICON.info } },
  key() { return HISTORY_KEY_PREFIX + historyScope(); },
  /** newest first: [{t: epoch ms, type, title, msg}] */
  list() { return readHistory().slice(); },
  push(entry) {
    const e = {
      t: Date.now(),
      type: TOAST_TITLE[entry && entry.type] ? entry.type : 'info',
      title: entry && entry.title != null ? String(entry.title) : '',
      msg: entry && entry.msg != null ? String(entry.msg) : '',
    };
    const arr = readHistory();
    arr.unshift(e);
    if (arr.length > TOAST_HISTORY_MAX) arr.length = TOAST_HISTORY_MAX;
    writeHistory(arr);
    notifyHistory('push', e);
    return e;
  },
  clear() { writeHistory([]); notifyHistory('clear', null); },
  subscribe(fn) { if (typeof fn === 'function') historySubs.add(fn); return () => historySubs.delete(fn); },
};

function renderMsg(el, msg, html) {
  const s = msg == null ? '' : String(msg);              // null-safe, defensive
  const useHtml = html === true || (html === undefined && messager.defaults.html === true);
  if (useHtml) el.innerHTML = s;
  else el.textContent = s;                               // default: escape
}

/* THE BOX DESIGN (message / confirm / prompt boxes; toasts have their own card):
   the ics window frame with the flat brand-gold title bar
   and NO corner close chip (the footer buttons and Escape are the only ways out - a
   message box has exactly one way out per answer); message body on the surface;
   footer = a soft band with the buttons RIGHT-aligned, each an own ics-button
   (`<button data-ics="button" data-icon="…">` mounted through $.fn.icsButton when the
   button engine is on the page) carrying the ok-check /
   close-cancel glyph: OK = ✓, Yes = ✓, No / Cancel = ✕; a destructive Yes keeps
   the red face (severity, not a tier). Widths: message 380, confirm/prompt 400
   (capped to the viewport by the window). Paint: css/components/ics-messager.css.

   BUTTON ORDER (app-wide): the AFFIRMATIVE button comes
   FIRST - Ok / Yes / Confirm on the LEFT, the negative one - No / Cancel / Close -
   on its RIGHT. The order is the DOM order of opts.buttons, so every caller that
   passes its own `buttons` array must list the positive answer first; the footer
   stays right-aligned as a block. ⚠ opts.focusValue selects the focused button BY VALUE, never by
   position - do not "fix" it to an index when a caller reorders. */
const BOX_WIDTH = { message: 380, confirm: 400 };

/** mount the footer buttons as own ics-buttons (idempotent; no engine = the sheet
    still paints the .ics-btn face, only the glyph is missing) */
function mountButtons(btnRow) {
  const jq = globalThis.jQuery || globalThis.$;
  if (!jq || !jq.fn || typeof jq.fn.icsButton !== 'function') return false;
  try { jq(btnRow).children('[data-ics="button"]').icsButton(); return true; }
  catch (e) { console.error('[ics] messager button mount failed', e); return false; }
}

/**
 * One alert/confirm box. buttons: [{text, value[, cls][, icon]}] - a click records
 * `pressed` + result then closes; fn fires ONLY when pressed.
 */
function dialog(opts) {
  const prevFocus = typeof document !== 'undefined' ? document.activeElement : null;   // restored on dismiss
  const entry = { pressed: false, result: undefined };

  const src = document.createElement('div');
  src.className = 'ics-messager__boxsrc';
  const body = document.createElement('div');
  body.className = 'messager-body';                      // PUBLIC class: page overrides target it
  if (opts.icon) {
    const ic = document.createElement('div');
    ic.className = 'messager-icon messager-' + opts.icon;
    body.appendChild(ic);
  }
  const msgEl = document.createElement('div');
  msgEl.className = 'ics-messager__msg';
  renderMsg(msgEl, opts.msg, opts.html);
  body.appendChild(msgEl);

  // PROMPT INPUT - present ONLY when opts.prompt is given (IcsPrompt).
  // The typed value is captured at CLICK time and handed to fn() as its SECOND
  // argument, so a caller that reads only the first argument is unaffected.
  let inputEl = null;
  if (opts.prompt) {
    inputEl = document.createElement('input');
    inputEl.type = 'text';
    inputEl.className = 'ics-messager__input';
    inputEl.autocomplete = 'off';
    inputEl.value = opts.prompt.value == null ? '' : String(opts.prompt.value);
    if (opts.prompt.placeholder) inputEl.placeholder = opts.prompt.placeholder;
    if (opts.prompt.maxlength) inputEl.maxLength = opts.prompt.maxlength;
    inputEl.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;                     // Esc is handled by keyHandler below
      e.preventDefault();                                // Enter = OK
      const ok = btnRow.querySelector('.ics-messager__btn--yes');
      if (ok) ok.click();
    });
    body.appendChild(inputEl);
  }

  const clr = document.createElement('div');
  clr.style.clear = 'both';
  body.appendChild(clr);
  src.appendChild(body);

  const btnRow = document.createElement('div');
  btnRow.className = 'messager-button';                  // PUBLIC footer class
  for (const b of opts.buttons) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ics-btn ics-messager__btn' + (b.cls ? ' ' + b.cls : '');
    btn.setAttribute('data-ics', 'button');              // own ics-button (glyph + face from the engine/sheet)
    if (b.icon) btn.setAttribute('data-icon', b.icon);
    btn.textContent = b.text;                            // the engine reads the label from the inner HTML
    btn.addEventListener('click', () => {
      entry.pressed = true;
      entry.result = b.value;
      if (inputEl) entry.input = inputEl.value;          // BEFORE teardown removes the element
      win.close();
    });
    btnRow.appendChild(btn);
  }
  src.appendChild(btnRow);
  document.body.appendChild(src);
  mountButtons(btnRow);

  let finished = false;
  const finish = () => {                                 // runs from Window.onClose (mask already hidden)
    if (finished) return;
    finished = true;
    document.removeEventListener('keydown', keyHandler, true);
    const i = boxes.indexOf(entry);
    if (i !== -1) boxes.splice(i, 1);
    win.destroy();                                       // fresh elements per call: leak-free teardown
    if (src.parentNode) src.parentNode.removeChild(src);
    if (prevFocus && prevFocus.focus && document.contains(prevFocus)) prevFocus.focus();   // focus goes back where it was
    if (entry.pressed && typeof opts.fn === 'function') opts.fn(entry.result, entry.input);   // the box closes BEFORE fn runs
  };

  const win = new Window(src, {
    title: opts.title || '',
    noheader: !opts.title,                               // no title => no header
    modal: true,
    closable: false,                                     // no corner chip: the footer answers, Esc dismisses
    box: true,                                           // never a close-topmost target: a caller's icsWindow('close') right after IcsConfirm/IcsAlert closes ITS window, not this box
    draggable: true,
    resizable: false, collapsible: false, minimizable: false, maximizable: false,
    width: opts.width || BOX_WIDTH.message, closed: true,
    onClose: finish,
  });
  win.wrap.classList.add('messager-window', 'ics-messager');   // hooks for page CSS and tests

  // per-instance document handler, topmost box only; Enter = native
  // button activation (the affirmative button is focused on open)
  const keyHandler = (e) => {
    if (boxes[boxes.length - 1] !== entry) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      WindowStackService.noteEscConsumed();              // one ESC closes exactly one thing
      if (opts.escValue !== undefined) {                 // Esc = pressing that button
        entry.pressed = true;
        entry.result = opts.escValue;
      }                                                  // else: plain dismiss, no fn
      win.close(); return;
    }
    if (e.key === 'Tab') {
      // the prompt input joins the ring, else it would be unreachable by keyboard
      const ring = [...(inputEl ? [inputEl] : []), ...btnRow.querySelectorAll('button')];
      if (!ring.length) return;
      e.preventDefault();                                // focus trapped in the box
      const at = ring.indexOf(document.activeElement);
      const next = e.shiftKey ? (at - 1 + ring.length) % ring.length : (at + 1) % ring.length;
      ring[next].focus();
    }
  };
  document.addEventListener('keydown', keyHandler, true);
  boxes.push(entry);

  win.open();                                            // centers, on a fresh z pair above any open window
  if (inputEl) {
    inputEl.focus();
    inputEl.select();                                    // the default value arrives selected
  } else {
    let focusBtn = btnRow.querySelector('button');       // default: the first button (the affirmative)
    if (opts.focusValue !== undefined) {
      const at = opts.buttons.findIndex((b) => b.value === opts.focusValue);
      const all = btnRow.querySelectorAll('button');
      if (at !== -1 && all[at]) focusBtn = all[at];
    }
    if (focusBtn) focusBtn.focus();                      // Enter activates the focused button
  }
  return win;
}

/**
 * THE toast. options: {type, msg, title, timeout, html, onClick} (normToast fills
 * the defaults). Returns the handle {el, type, title, close(), closed} - a toast is
 * plain DOM, not a Window: it never enters the window stack, is never a
 * close-topmost / Escape target, and shows no mask (an IcsToastOk(...) followed by
 * icsWindow('close') still closes the CALLER's window).
 */
function toast(options) {
  const o = normToast(options);
  const host = getToastHost();

  const card = document.createElement('div');
  card.className = 'ics-toast ics-toast--' + o.type + ' ics-messager--toast ics-messager--' + o.type;
  card.setAttribute('role', o.type === 'error' || o.type === 'warning' ? 'alert' : 'status');
  const icon = document.createElement('i');
  icon.className = 'ics-toast__icon ics-icon-' + TOAST_ICON[o.type];   // own ics-icons glyph; the badge is painted by the sheet
  icon.setAttribute('aria-hidden', 'true');
  const body = document.createElement('div');
  body.className = 'ics-toast__body';
  const titleEl = document.createElement('div');
  titleEl.className = 'ics-toast__title';
  titleEl.textContent = o.title;
  const msgEl = document.createElement('div');
  msgEl.className = 'ics-toast__msg ics-messager__msg';   // .ics-messager__msg = the family's shared message selector (also a test probe hook)
  renderMsg(msgEl, o.msg, o.html);
  body.appendChild(titleEl);
  body.appendChild(msgEl);
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'ics-toast__close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.textContent = '×';
  card.appendChild(icon);
  card.appendChild(body);
  card.appendChild(closeBtn);

  let timer = null, finished = false;
  const clearTimer = () => { if (timer) { clearTimeout(timer); timer = null; } };
  const finish = () => {
    if (finished) return;
    finished = true;
    clearTimer();
    const i = liveToasts.indexOf(handle);
    if (i !== -1) liveToasts.splice(i, 1);
    card.classList.add('ics-toast--out');
    setTimeout(() => { if (card.parentNode) card.parentNode.removeChild(card); }, TOAST_ANIM_MS);
  };
  const arm = () => { if (o.timeout > 0) timer = setTimeout(finish, o.timeout); };
  card.addEventListener('mouseenter', clearTimer);                                    // hover pauses the timer
  card.addEventListener('mouseleave', () => { clearTimer(); if (!finished) arm(); });   // FULL restart
  closeBtn.addEventListener('click', (e) => { e.stopPropagation(); finish(); });
  if (typeof o.onClick === 'function') {
    card.classList.add('ics-toast--clickable');
    card.addEventListener('click', () => {
      finish();
      try { o.onClick(); } catch (e) { console.error('[ics] toast onClick failed', e); }
    });
  }
  // NO key bindings: ESC must not close a toast

  const handle = { el: card, type: o.type, title: o.title, close: finish, get closed() { return finished; } };
  while (liveToasts.length >= TOAST_MAX_VISIBLE) liveToasts[0].close();   // the oldest makes room
  liveToasts.push(handle);
  card.classList.add('ics-toast--pre');
  host.appendChild(card);
  void card.offsetHeight;                                // reflow so the arrival transition runs
  card.classList.remove('ics-toast--pre');
  arm();
  toastHistory.push({ type: o.type, title: o.title, msg: msgEl.textContent });
  return handle;
}

/** a read-only view of the open cards, for test hooks (underscore = not API) */
export function _openToasts() { return liveToasts.slice(); }

export const messager = {
  /** the one i18n / html seam: captions and the escape default live here */
  defaults: { ok: 'Ok', cancel: 'Cancel', html: false },

  /** alert(title, msg[, icon][, fn]) - icon: 'warning'|'error'|'info' */
  alert(title, msg, icon, fn) {
    return dialog({
      title, msg, icon: icon || null, fn,
      buttons: [{ text: messager.defaults.ok, value: undefined, icon: 'ok-check', cls: 'ics-messager__btn--yes' }],   // fn(undefined) on Ok
    });
  },

  /** confirm(title, msg, fn) - Ok->fn(true), Cancel->fn(false), dismissal->nothing;
      Escape presses Cancel, so fn(false) runs */
  confirm(title, msg, fn) {
    return dialog({
      title, msg, icon: 'question', fn,
      escValue: false, focusValue: true, width: BOX_WIDTH.confirm,   // Ok left / Cancel right, Ok focused
      buttons: [
        { text: messager.defaults.ok, value: true, icon: 'ok-check', cls: 'ics-messager__btn--yes' },
        { text: messager.defaults.cancel, value: false, icon: 'close-cancel', cls: 'ics-messager__btn--no' },
      ],
    });
  },

  /** show({title, msg, timeout[, html]}) - adapter seam over THE toast (info face;
      showType is accepted and ignored - the card has one arrival motion) */
  show(options) {
    const o = options || {};
    return toast({ type: 'info', title: o.title, msg: o.msg, timeout: o.timeout, html: o.html });
  },

  /* internals exposed for test hooks (underscore = not API) */
  _escape: escapeHtml,
  _normToast: normToast,
};

/** boot hook: publish the statics as $.messager IF that name is free; an existing
    implementation is never clobbered. Returns whether the claim landed. */
export function attachMessager($) {
  if (!$ || $.messager) {
    if ($ && $.messager && $.messager !== messager) {
      console.warn('[ics] $.messager already defined by another script - the ics messager did not claim the name');
    }
    return false;
  }
  $.messager = messager;
  return true;
}

/* =============================================================================
   THE PUBLIC GLOBALS - signatures and defaults
   =============================================================================
   - IcsToast(sType, sMsg[, nMs | sTitle | {title, timeout, onClick, html}])
     timeout = nMs, else error 5200 ms / everything else 3400 ms; title = the
     severity default unless the call passes one.
   - IcsToastOk / IcsToastErr / IcsToastWarn / IcsToastInfo
       (sMsg[, sTitle | {title, timeout, onClick, html}])
     the everyday form: severity in the function name, one required argument.
   - IcsMsgBox(sTitle, sMsg[, fnOk])   title defaults to 'Message'; ONE OK
     button; Escape presses it, so fnOk runs.
   - IcsConfirm(sMsg, fOnYes[, {title, yesText, noText, danger, onNo}])
     Escape presses No, so onNo runs; `danger: true` paints the destructive Yes
     red; Yes left / No right with Yes focused; the box closes BEFORE the
     callback runs.
   The globals are installed at boot and re-asserted from the ready queue in the
   bundle entry, so a later script that declares the same names does not win.
   ============================================================================= */

/** the optional 3rd/2nd argument of the toast family: a number = timeout ms, a string =
    title, an object = {title, timeout, onClick, html}; anything else = defaults - pure.
    ⚠ THE TWO FORMS READ `0` DIFFERENTLY, deliberately:
      IcsToast(type, msg, 0)         the positional shape reads `nMs || default`, so a 0 there
                                     means "not specified" and the caller gets the default
                                     timeout - screens rely on that.
      IcsToastX(msg, {timeout: 0})   the explicit shape: 0 means NEVER auto-close (a sticky toast
                                     the user dismisses), which is what the engine's `timeout > 0`
                                     arming test already honours. Without this split a caller
                                     could not ask for a sticky toast at all. */
export function normToastArg(a) {
  if (typeof a === 'number') return { timeout: a > 0 ? a : undefined };
  if (typeof a === 'string') return { title: a };
  if (a && typeof a === 'object') {
    const ms = a.timeout;
    return {
      title: typeof a.title === 'string' ? a.title : undefined,
      timeout: typeof ms === 'number' && !isNaN(ms) && ms >= 0 ? ms : undefined,
      onClick: typeof a.onClick === 'function' ? a.onClick : null,
      html: a.html === true ? true : undefined,
    };
  }
  return {};
}

/** IcsToast('success'|'error'|'warning'|'info', sMsg[, nMs | sTitle | oOpt]) - THE toast */
export function IcsToast(sType, sMsg, oArg) {
  const a = normToastArg(oArg);
  return toast({ type: sType, msg: sMsg, title: a.title, timeout: a.timeout, onClick: a.onClick, html: a.html });
}
export function IcsToastOk(m, oArg) { return IcsToast('success', m, oArg); }
/** blank-message fallback - DELIBERATELY the opposite of the modal rule below: an error
    worth reporting must never vanish. jQuery's 3rd ajax error argument is empty on an
    aborted or blocked request, which is how a blank message arrives here. */
export function IcsToastErr(m, oArg) {
  const s = (m === null || m === undefined) ? '' : String(m);
  return IcsToast('error', s.trim() === ''
    ? 'The request could not be completed. Please try again.' : m, oArg);
}
export function IcsToastWarn(m, oArg) { return IcsToast('warning', m, oArg); }
export function IcsToastInfo(m, oArg) { return IcsToast('info', m, oArg); }

/** IcsMsgBox(sTitle, sMsg[, fnOk]) - the refused-click gate; Escape presses OK */
export function IcsMsgBox(sTitle, sMsg, fnOk) {
  // A message with NO CONTENT is not a message - open no box.
  // ⚠ fnOk STILL RUNS: the callback carries the flow the OK click would have released
  // (navigation, reload, window.close, submit). Swallowing it with the box would strand
  // the screen.
  const sText = (sMsg === null || sMsg === undefined) ? '' : String(sMsg);
  if (sText.trim() === '') {
    if (typeof fnOk === 'function') fnOk();
    return null;
  }
  return dialog({
    title: sTitle || 'Message', msg: sMsg,
    fn: () => { if (typeof fnOk === 'function') fnOk(); },
    buttons: [{ text: 'OK', value: true, icon: 'ok-check', cls: 'ics-messager__btn--yes' }],
    escValue: true, focusValue: true, width: BOX_WIDTH.message,
  });
}

/** IcsAlert(sMsg[, fnOk]) - the one-button message box, titled 'Message'.
    The everyday notice: a refused click, a rule the user must read. It does not
    block, so anything that must wait for the click goes in fnOk. */
export function IcsAlert(sMsg, fnOk) { return IcsMsgBox('Message', sMsg, fnOk); }

/** pure option normalizer for IcsConfirm - fills the defaults */
export function normConfirmOpt(oOpt) {
  const o = oOpt || {};
  return {
    title: o.title || 'Confirmation',
    yesText: o.yesText || 'Yes',
    noText: o.noText || 'No',
    danger: o.danger === true,
    onNo: typeof o.onNo === 'function' ? o.onNo : null,
  };
}

/** IcsConfirm(sMsg, fOnYes[, oOpt]) - the product's ONE consent dialog */
export function IcsConfirm(sMsg, fOnYes, oOpt) {
  const o = normConfirmOpt(oOpt);
  return dialog({
    title: o.title, msg: sMsg,
    fn: (v) => {
      if (v === true) { if (typeof fOnYes === 'function') fOnYes(); }
      else if (v === false && o.onNo) o.onNo();
    },
    buttons: [                                                       // Yes left / No right
      { text: o.yesText, value: true, icon: 'ok-check', cls: 'ics-messager__btn--yes' + (o.danger ? ' ics-danger' : '') },
      { text: o.noText, value: false, icon: 'close-cancel', cls: 'ics-messager__btn--no' },
    ],
    escValue: false, focusValue: true, width: BOX_WIDTH.confirm,   // Esc = No; Yes focused
  });
}

/** IcsPrompt(sMsg, sDefault, fnOk[, {title, okText, cancelText, placeholder, maxlength, onCancel}])
    The one-line input box, on the same substrate as the rest of the family.
    fnOk(sValue) fires ONLY on OK and receives the RAW, UNTRIMMED string, so a caller's
    own validation sees exactly what was typed.
    ⚠ There is no `null` answer: Cancel and Escape take the oOpt.onCancel branch instead -
    "the user declined" is a different BRANCH, not a different value. */
export function IcsPrompt(sMsg, sDefault, fnOk, oOpt) {
  const o = oOpt || {};
  return dialog({
    title: o.title || 'Message', msg: sMsg,
    prompt: { value: sDefault, placeholder: o.placeholder, maxlength: o.maxlength },
    fn: (v, sVal) => {
      if (v === true) { if (typeof fnOk === 'function') fnOk(sVal); }
      else if (v === false && typeof o.onCancel === 'function') o.onCancel();
    },
    buttons: [                                                       // OK left / Cancel right
      { text: o.okText || 'OK', value: true, icon: 'ok-check', cls: 'ics-messager__btn--yes' },
      { text: o.cancelText || 'Cancel', value: false, icon: 'close-cancel', cls: 'ics-messager__btn--no' },
    ],
    escValue: false, width: BOX_WIDTH.confirm,           // Esc ≡ Cancel (family-wide rule)
  });
}

/* =============================================================================
   IcsMsg - THE DICTIONARY CALL. Say a message by KEY; the dictionary decides the
   wording, the severity and the appearance.
   =============================================================================
       IcsMsg('validation.selectItemS')
       IcsMsg('validation.selectItemS', { what: 'a buyer' })
       IcsMsg('confirm.confirmDelete', function () { deleteIt(); })
       IcsMsg('confirm.confirm', { action: 'archive' }, function () { archive(); })

   ARGUMENTS are positional but shape-detected, because the everyday call has no
   tokens and the everyday consent call has no tokens either:
       IcsMsg(sKey[, oTokens][, fnPrimary | oOpt])
   A FUNCTION in either optional slot is the primary callback - fnOk for a modal,
   fnYes for a confirm, fnOk(sValue) for a prompt, ignored by a toast (a toast
   has no answer to give). An OBJECT in the last slot is options, passed to the
   underlying call as-is, plus `onYes` / `onOk` for the callback and `value` for
   a prompt's default. Anything a call site sets there BEATS the dictionary -
   the dictionary is the default, never a cage.

   APPEARANCE (ruling D4: toast-first, modal ONLY for a refused click) is the
   ENTRY'S, not the call site's: `toast` -> IcsToast of the entry's severity ·
   `modal` -> IcsMsgBox · `confirm` -> IcsConfirm · `prompt` -> IcsPrompt. That
   is the whole point of the key - a screen stops deciding how loud a message is,
   so the same concept never appears as a modal on one screen and a toast on the
   next. Override for one call with `{ appearance: 'modal' }` when a screen
   genuinely differs, and expect to justify it.

   WHEN THE KEY IS WRONG, SOMETHING IS STILL SHOWN. An unknown key, or a key that
   resolves to a component caption rather than a message, is a CALLER BUG - so it
   is reported to the console AND shown as a warning toast carrying whatever text
   resolved (the key itself, at worst). Silence would hide the bug from the user
   and from the developer at the same time; the i18n kernel's never-blank rule
   applies here exactly as it does to a caption.
   ============================================================================= */

/** severity -> toast face. A `question` never reaches here through a dictionary
    entry (D4 makes it a confirm), but an appearance override could send it, and a
    question is a warning to look at rather than a success or an error. */
const MSG_TOAST_TYPE = { error: 'error', warning: 'warning', success: 'success', info: 'info', question: 'warning' };

/** split IcsMsg's two optional arguments into {tokens, fn, opt} - pure */
export function normMsgArgs(a, b) {
  const out = { tokens: null, fn: null, opt: {} };
  const take = (x) => {
    if (x === null || x === undefined) return;
    if (typeof x === 'function') { out.fn = x; return; }
    if (typeof x !== 'object') return;
    /* an options bag is told from a token bag by carrying a KNOWN option name;
       tokens are free-form words from the message text, so they cannot be
       enumerated - the known list can. */
    const known = ['title', 'appearance', 'severity', 'yesText', 'noText', 'okText', 'cancelText',
      'danger', 'onNo', 'onCancel', 'onYes', 'onOk', 'value', 'placeholder', 'maxlength',
      'timeout', 'onClick', 'html'];
    if (Object.keys(x).some((k) => known.indexOf(k) !== -1)) out.opt = x;
    else if (!out.tokens) out.tokens = x;
  };
  take(a); take(b);
  return out;
}

/** IcsMsg(sKey[, oTokens][, fn | oOpt]) - the dictionary call */
export function IcsMsg(sKey, a, b) {
  const { tokens, fn, opt } = normMsgArgs(a, b);
  const entry = MessageCatalogService.entry(sKey);

  if (!entry) {
    /* unknown, or a caption used as a message. Report it and still speak. */
    const text = MessageCatalogService.t(sKey, tokens);
    console.error('[ics] IcsMsg: "' + sKey + '" is not a message key in the dictionary' +
      (text === sKey ? ' (and resolves to nothing)' : ' (it resolves to a caption: "' + text + '")'));
    return IcsToastWarn(text, opt);
  }

  const text = MessageCatalogService.render(entry, tokens);
  /* a required slot nobody filled survives as {slot} - visible on purpose, and
     named here so it is diagnosable without reading the rendered sentence */
  const unfilled = (entry.requiredTokens || []).filter((n) => text.indexOf('{' + n + '}') !== -1);
  if (unfilled.length) {
    console.error('[ics] IcsMsg: "' + sKey + '" needs token(s) ' + unfilled.join(', ') +
      ' - the call passed none, so the slot is shown literally');
  }

  const appearance = opt.appearance || entry.appearance;
  const title = opt.title || entry.title || undefined;
  const cb = fn || opt.onYes || opt.onOk || null;

  if (appearance === 'confirm') {
    return IcsConfirm(text, cb, Object.assign({}, opt, title ? { title } : {}));
  }
  if (appearance === 'prompt') {
    return IcsPrompt(text, opt.value === undefined ? '' : opt.value, cb, Object.assign({}, opt, title ? { title } : {}));
  }
  if (appearance === 'modal') {
    return IcsMsgBox(title || 'Message', text, cb);
  }
  /* toast - the D4 default, and the only appearance with no answer to give */
  const sev = opt.severity || entry.severity;
  return IcsToast(MSG_TOAST_TYPE[sev] || 'info', text, Object.assign({}, opt, title ? { title } : {}));
}

/* =============================================================================
   IcsModalDialog - the modal PAGE dialog: a whole child page shown inside a modal
   window of this application, never a popup window. The child renders in an
   <iframe> and, because the frame is same-origin, keeps the full page-dialog
   contract:
     window.dialogArguments   = the 2nd argument (set on the frame's Window
                                BEFORE navigation - the initial about:blank
                                Window is reused for a same-origin document;
                                re-applied on every load in case the child
                                navigates)
     window.returnValue       = whatever the child assigned, read at close
     window.close()           = closes THIS dialog (an own property shadows the
                                Window method - a framed page's own close() is a
                                no-op otherwise)
     window.opener            = the host page (assignable), so a child calling
                                window.opener.SomeHostFunction(...) still lands
   Result delivery: `fnDone(returnValue)` runs AFTER the dialog is closed and
   destroyed (child closed itself, host X / Escape, or code) - `null` when the
   child set nothing. A Promise resolving to the same value is returned too.
   Non-blocking like the rest of the family: the statement after
   IcsModalDialog(...) runs immediately, so everything that depends on the
   answer belongs in fnDone.
   Features string = the `dialogWidth:800px;dialogHeight:600px;dialogLeft:..;
   dialogTop:..` grammar; other keys (center/status/scroll/resizable) are
   accepted and ignored. The window centers itself and is draggable, resizable,
   modal and closable; Escape closes it, because this is a plain window and not
   a message box.
   ============================================================================= */
export function parseDialogFeatures(str) {
  const o = {};
  String(str || '').split(';').forEach((p) => {
    const kv = p.split(/[:=]/);
    if (kv.length < 2) return;
    const key = kv[0].trim().toLowerCase();
    const num = parseInt(String(kv[1]).trim().replace(/px$/i, ''), 10);
    if (isNaN(num)) return;
    if (key === 'dialogwidth') o.width = num;
    else if (key === 'dialogheight') o.height = num;
    else if (key === 'dialogleft') o.left = num;
    else if (key === 'dialogtop') o.top = num;
  });
  return o;
}

export function IcsModalDialog(url, arg, features, fnDone) {
  const f = parseDialogFeatures(features);
  const vw = document.documentElement.clientWidth, vh = document.documentElement.clientHeight;
  const width = Math.min(f.width || 800, Math.max(320, vw - 20));
  const height = Math.min(f.height || 600, Math.max(200, vh - 20));

  return new Promise((resolve) => {
    const src = document.createElement('div');
    src.className = 'ics-messager__boxsrc ics-modal-dialog';
    const frame = document.createElement('iframe');
    frame.className = 'ics-modal-dialog__frame';
    frame.setAttribute('frameborder', '0');
    src.appendChild(frame);
    document.body.appendChild(src);

    let rv = null, finished = false;
    const finish = () => {                                 // from Window.onClose (X, Escape, child close(), code)
      if (finished) return;
      finished = true;
      try { rv = frame.contentWindow && frame.contentWindow.returnValue; } catch (e) { /* cross-origin: no value */ }
      if (rv === undefined) rv = null;
      win.destroy();
      if (src.parentNode) src.parentNode.removeChild(src);
      resolve(rv);
      if (typeof fnDone === 'function') fnDone(rv);        // close BEFORE the callback (the family rule)
    };
    const win = new Window(src, {
      title: '', noheader: false,
      modal: true, closable: true, draggable: true, resizable: true,
      collapsible: false, minimizable: false, maximizable: false,
      width, height, closed: true,
      onClose: finish,
    });
    win.wrap.classList.add('ics-modal-dialog__window');

    /* the page-dialog contract - applied on the initial Window (reused for a
       same-origin document) and again on every load (child-side navigation) */
    const arm = () => {
      let cw = null;
      try { cw = frame.contentWindow; } catch (e) { cw = null; }
      if (!cw) return;
      try { cw.dialogArguments = arg; } catch (e) { /* cross-origin */ }
      try { cw.opener = window; } catch (e) { /* not assignable here */ }
      try { cw.close = () => { win.close(); }; } catch (e) { /* cross-origin */ }
    };
    frame.addEventListener('load', () => {
      arm();
      /* the child's <title> names the dialog */
      try {
        const t = frame.contentDocument && frame.contentDocument.title;
        if (t) win.setTitle(escapeHtml(t));
      } catch (e) { /* cross-origin: keep blank */ }
    });
    win.open();
    arm();                                                 // on the initial about:blank Window - reused by the same-origin document
    frame.src = url;
  });
}

export const messagerBridge = { IcsToast, IcsToastOk, IcsToastErr, IcsToastWarn, IcsToastInfo, IcsMsgBox, IcsAlert, IcsConfirm, IcsPrompt, IcsModalDialog, IcsMsg };

/** assign the ELEVEN public globals onto g; returns how many were written.
    The bundle entry (index.dialogs.js) calls this at boot AND from the ready
    queue, so a later script that re-declares one of these names does not win.
    ONE module serves every shell: a symbol added here reaches all of them at the
    next build. */
export function attachMessagerBridge(g) {
  const t = g || globalThis;
  let n = 0;
  for (const k of Object.keys(messagerBridge)) { t[k] = messagerBridge[k]; n++; }
  return n;
}
